import { Facebook, Globe, Instagram, Linkedin, LucideIcon, Music2, Twitter, Youtube } from 'lucide-react';

export interface AdFormat {
  id: string;
  label: string;
  /** Krótki opis, gdzie kreacja się pokaże. */
  hint: string;
  width: number;
  height: number;
  /**
   * Marginesy (w px formatu), które platforma zasłania własnym interfejsem.
   * Rysujemy je na planszy jako linie pomocnicze.
   */
  safeArea?: { top: number; bottom: number; left: number; right: number };
}

export interface AdPlatform {
  id: string;
  name: string;
  icon: LucideIcon;
  /** Kolor akcentu kafelka platformy. */
  color: string;
  description: string;
  formats: AdFormat[];
}

const VERTICAL_SAFE_AREA = { top: 250, bottom: 420, left: 60, right: 160 };

export const PLATFORMS: AdPlatform[] = [
  {
    id: 'facebook',
    name: 'Facebook',
    icon: Facebook,
    color: '#1877F2',
    description: 'Posty, reklamy i relacje',
    formats: [
      { id: 'fb-square', label: 'Post kwadratowy', hint: 'Feed, reklamy karuzelowe', width: 1080, height: 1080 },
      { id: 'fb-link', label: 'Post z linkiem', hint: 'Reklama z odnośnikiem', width: 1200, height: 630 },
      { id: 'fb-story', label: 'Relacja', hint: 'Stories i Reels', width: 1080, height: 1920, safeArea: VERTICAL_SAFE_AREA },
      { id: 'fb-cover', label: 'Zdjęcie w tle', hint: 'Okładka strony', width: 1640, height: 664 },
    ],
  },
  {
    id: 'instagram',
    name: 'Instagram',
    icon: Instagram,
    color: '#E1306C',
    description: 'Feed, karuzele i relacje',
    formats: [
      { id: 'ig-square', label: 'Post kwadratowy', hint: 'Klasyczny wpis 1:1', width: 1080, height: 1080 },
      { id: 'ig-portrait', label: 'Post pionowy', hint: 'Zajmuje więcej ekranu, 4:5', width: 1080, height: 1350 },
      { id: 'ig-story', label: 'Relacja / Reels', hint: 'Pełny ekran 9:16', width: 1080, height: 1920, safeArea: VERTICAL_SAFE_AREA },
    ],
  },
  {
    id: 'linkedin',
    name: 'LinkedIn',
    icon: Linkedin,
    color: '#0A66C2',
    description: 'Komunikacja biznesowa',
    formats: [
      { id: 'li-square', label: 'Post kwadratowy', hint: 'Feed 1:1', width: 1200, height: 1200 },
      { id: 'li-link', label: 'Post z linkiem', hint: 'Podgląd artykułu 1.91:1', width: 1200, height: 627 },
      { id: 'li-story', label: 'Pionowy', hint: 'Reklama pełnoekranowa', width: 1080, height: 1920, safeArea: VERTICAL_SAFE_AREA },
      { id: 'li-cover', label: 'Baner firmowy', hint: 'Okładka profilu firmy', width: 1584, height: 396 },
    ],
  },
  {
    id: 'tiktok',
    name: 'TikTok',
    icon: Music2,
    color: '#010101',
    description: 'Wideo i okładki pionowe',
    formats: [
      { id: 'tt-video', label: 'Okładka wideo', hint: 'Pełny ekran 9:16', width: 1080, height: 1920, safeArea: VERTICAL_SAFE_AREA },
      { id: 'tt-cover', label: 'Miniatura profilu', hint: 'Kadr widoczny na siatce', width: 1080, height: 1440 },
    ],
  },
  {
    id: 'x',
    name: 'X',
    icon: Twitter,
    color: '#111111',
    description: 'Krótkie komunikaty',
    formats: [
      { id: 'x-post', label: 'Post', hint: 'Obraz w osi czasu 16:9', width: 1600, height: 900 },
      { id: 'x-square', label: 'Post kwadratowy', hint: 'Feed 1:1', width: 1080, height: 1080 },
      { id: 'x-header', label: 'Baner profilu', hint: 'Nagłówek konta', width: 1500, height: 500 },
    ],
  },
  {
    id: 'youtube',
    name: 'YouTube',
    icon: Youtube,
    color: '#FF0000',
    description: 'Miniatury i Shorts',
    formats: [
      { id: 'yt-thumb', label: 'Miniatura', hint: 'Kafelek filmu 16:9', width: 1280, height: 720 },
      { id: 'yt-shorts', label: 'Shorts', hint: 'Pionowe wideo 9:16', width: 1080, height: 1920, safeArea: VERTICAL_SAFE_AREA },
    ],
  },
  {
    id: 'google',
    name: 'Google Ads',
    icon: Globe,
    color: '#34A853',
    description: 'Banery w sieci reklamowej',
    formats: [
      { id: 'ga-leaderboard', label: 'Leaderboard', hint: 'Baner poziomy 728x90', width: 728, height: 90 },
      { id: 'ga-rectangle', label: 'Prostokąt', hint: 'Kafelek w treści 300x250', width: 300, height: 250 },
      { id: 'ga-skyscraper', label: 'Wieżowiec', hint: 'Baner boczny 160x600', width: 160, height: 600 },
      { id: 'ga-responsive', label: 'Responsywna', hint: 'Grafika pozioma 1200x628', width: 1200, height: 628 },
    ],
  },
];

export const findPlatform = (platformId: string): AdPlatform | undefined => (
  PLATFORMS.find((platform) => platform.id === platformId)
);

export const findFormat = (platformId: string, formatId: string): AdFormat | undefined => (
  findPlatform(platformId)?.formats.find((format) => format.id === formatId)
);

/** Proporcje kafelka podglądu formatu, ograniczone żeby skrajne banery nie rozjeżdżały siatki. */
export const previewRatio = (format: AdFormat): number => (
  Math.min(Math.max(format.width / format.height, 0.45), 2.4)
);
